/**
 * Sentry configuration and utilities for production-ready error tracking
 * Follows existing codebase patterns for error handling and logging
 */

import * as Sentry from "@sentry/react";

interface SentryConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: number;
  enableLogs?: boolean;
}

class SentryManager {
  private static instance: SentryManager;
  private isInitialized = false;
  // Remove unused logger property

  public static getInstance(): SentryManager {
    if (!SentryManager.instance) {
      SentryManager.instance = new SentryManager();
    }
    return SentryManager.instance;
  }

  /**
   * Initialize Sentry with production-ready configuration
   */
  public initialize(config: SentryConfig = {}): void {
    if (this.isInitialized) {
      return;
    }

    const dsn = config.dsn || import.meta.env.VITE_SENTRY_DSN;

    // Debug logging (no DSN exposure)
    if (import.meta.env.DEV) {
      console.log("🔍 Sentry Debug Info:");
      console.log(
        "- DSN:",
        import.meta.env.VITE_SENTRY_DSN ? "Configured" : "Missing"
      );
      console.log("- Environment:", import.meta.env.MODE);
    }

    // Don't initialize if no DSN is provided (allows for easy kill switch)
    if (!dsn) {
      console.warn("❌ Sentry DSN not provided - error tracking disabled");
      return;
    }

    const environment =
      config.environment ||
      import.meta.env.VITE_SENTRY_ENVIRONMENT ||
      (import.meta.env.PROD ? "production" : "development");

    const release =
      config.release ||
      `selfserve-dashboard@${import.meta.env.PACKAGE_VERSION || "unknown"}`;

    // Set sample rate based on environment
    const tracesSampleRate =
      config.tracesSampleRate ?? (import.meta.env.PROD ? 0.1 : 1.0);

    try {
      Sentry.init({
        dsn,
        environment,
        release,
        tracesSampleRate,
        enableLogs: config.enableLogs ?? import.meta.env.DEV,

        integrations: [
          // Browser tracing for web vitals and navigation
          Sentry.browserTracingIntegration(),
          // Send console.log, console.error, and console.warn calls as logs to Sentry
          Sentry.consoleLoggingIntegration({
            levels: ["error", "warn"], // Only track errors and warnings to reduce noise
          }),
        ],

        beforeSend(event) {
          // Don't send events in development environment (use Sentry environment, not Vite DEV mode)
          if (environment === "development") {
            return null;
          }

          // Filter out known noisy/benign errors
          if (event.exception?.values?.[0]?.value) {
            const errorMessage = event.exception.values[0].value;

            // Common browser/framework noise
            const noisyPatterns = [
              /ResizeObserver loop limit exceeded/i,
              /Script error/i,
              /Non-Error promise rejection captured/i,
              /Loading chunk \d+ failed/i,
              /Network request failed/i,
            ];

            if (noisyPatterns.some((pattern) => pattern.test(errorMessage))) {
              return null;
            }
          }

          // Scrub PII and sensitive data
          if (event.request?.headers) {
            delete event.request.headers["Authorization"];
            delete event.request.headers["authorization"];
            delete event.request.headers["Cookie"];
            delete event.request.headers["cookie"];
          }

          // Remove sensitive URL parameters
          if (event.request?.url) {
            try {
              const url = new URL(event.request.url);
              url.searchParams.delete("token");
              url.searchParams.delete("access_token");
              url.searchParams.delete("refresh_token");
              event.request.url = url.toString();
            } catch {
              // Invalid URL, keep as is
            }
          }

          // Scrub sensitive data from extra context
          if (event.extra) {
            Object.keys(event.extra).forEach((key) => {
              if (typeof event.extra![key] === "string") {
                const value = event.extra![key] as string;
                // Remove potential tokens or sensitive data
                if (
                  value.includes("token") ||
                  value.includes("password") ||
                  value.includes("secret")
                ) {
                  event.extra![key] = "[REDACTED]";
                }
              }
            });
          }

          return event;
        },

        beforeSendTransaction(event) {
          // Don't send transactions in development
          if (import.meta.env.DEV) {
            return null;
          }
          return event;
        },
      });

      // Sentry logger is available via Sentry.logger after initialization

      this.isInitialized = true;
      if (import.meta.env.DEV) {
        console.log(
          `✅ Sentry initialized successfully for ${environment} environment`
        );
      }
    } catch (error) {
      console.error("Failed to initialize Sentry:", error);
    }
  }

  /**
   * Set user context for error tracking
   */
  public async setUserContext(user: {
    id?: string;
    email?: string;
    [key: string]: any;
  }): Promise<void> {
    if (!this.isInitialized) return;

    // Only include safe, non-PII identifiers
    const safeUser = {
      id: user.id,
      email: user.email ? await this.hashEmail(user.email) : undefined,
    };

    Sentry.setUser(safeUser);
  }

  /**
   * Set hotel and employee tags for context
   */
  public setHotelTags(context: {
    hotelId?: string;
    employeeId?: string;
    [key: string]: any;
  }): void {
    if (!this.isInitialized) return;

    if (context.hotelId) {
      Sentry.setTag("hotel_id", context.hotelId);
    }
    if (context.employeeId) {
      Sentry.setTag("employee_id", context.employeeId);
    }
  }

  /**
   * Capture API errors with relevant context
   */
  public captureApiError(error: any, meta: Record<string, any> = {}): void {
    if (!this.isInitialized) return;

    const tags = {
      component: "api-client",
      endpoint: meta.endpoint || meta.url,
      method: meta.method,
      status: meta.status,
      ...meta.tags,
    };

    // Safely extract response data, limiting size and scrubbing sensitive fields
    const getSafeResponseData = (responseData: any) => {
      if (!responseData || typeof responseData !== "object") {
        return responseData;
      }

      // Convert to string and check size
      const dataString = JSON.stringify(responseData);
      if (dataString.length > 1000) {
        return "[Response too large - truncated for privacy]";
      }

      // Create a safe copy, removing potentially sensitive fields
      const safeData = { ...responseData };
      const sensitiveKeys = [
        "password",
        "token",
        "secret",
        "key",
        "auth",
        "session",
        "cookie",
      ];

      Object.keys(safeData).forEach((key) => {
        if (
          sensitiveKeys.some((sensitive) =>
            key.toLowerCase().includes(sensitive)
          )
        ) {
          safeData[key] = "[REDACTED]";
        }
      });

      return safeData;
    };

    Sentry.withScope((scope) => {
      scope.setTag("error_type", "api");
      Object.keys(tags).forEach((key) => {
        if (tags[key] !== undefined) {
          scope.setTag(key, String(tags[key]));
        }
      });

      scope.setContext("api_request", {
        endpoint: meta.endpoint || meta.url,
        method: meta.method,
        status: meta.status,
        duration: meta.duration,
        requestId: meta.requestId,
        responseData: getSafeResponseData(meta.responseData),
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture network errors
   */
  public captureNetworkError(error: any, meta: Record<string, any> = {}): void {
    if (!this.isInitialized) return;

    Sentry.withScope((scope) => {
      scope.setTag("error_type", "network");
      scope.setTag("component", "network");

      scope.setContext("network_request", {
        url: meta.url,
        method: meta.method,
        type: meta.type,
        requestId: meta.requestId,
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture React render errors
   */
  public captureRenderError(
    error: Error,
    errorInfo: { componentStack?: string }
  ): void {
    if (!this.isInitialized) return;

    Sentry.withScope((scope) => {
      scope.setTag("error_type", "react");
      scope.setTag("component", "global-error-boundary");

      scope.setContext("react_error_boundary", {
        componentStack: errorInfo.componentStack,
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Capture global JavaScript errors
   */
  public captureGlobalError(error: any, meta: Record<string, any> = {}): void {
    if (!this.isInitialized) return;

    Sentry.withScope((scope) => {
      scope.setTag("error_type", meta.type || "javascript");
      scope.setTag("component", "global-error-handler");

      scope.setContext("global_error", {
        url: meta.url,
        userAgent: meta.userAgent,
        timestamp: meta.timestamp,
        type: meta.type,
      });

      Sentry.captureException(error);
    });
  }

  /**
   * Start a custom span for performance tracking
   */
  public startSpan<T>(
    spanOptions: { op: string; name: string; attributes?: Record<string, any> },
    callback: (span: Sentry.Span) => T
  ): T {
    if (!this.isInitialized) {
      // If Sentry is not initialized, execute the callback with a safe no-op span
      const noopSpan = {
        setAttribute: (_key: string, _value: unknown) => {},
        setAttributes: (_attrs: Record<string, unknown>) => {},
        end: () => {},
      } as unknown as Sentry.Span;
      return callback(noopSpan);
    }

    return Sentry.startSpan(
      {
        op: spanOptions.op,
        name: spanOptions.name,
        attributes: spanOptions.attributes,
      },
      callback
    );
  }

  /**
   * Start an async span for performance tracking
   */
  public async startAsyncSpan<T>(
    spanOptions: { op: string; name: string; attributes?: Record<string, any> },
    callback: (span: Sentry.Span) => Promise<T>
  ): Promise<T> {
    if (!this.isInitialized) {
      // If Sentry is not initialized, execute the callback with a safe no-op span
      const noopSpan = {
        setAttribute: (_key: string, _value: unknown) => {},
        setAttributes: (_attrs: Record<string, unknown>) => {},
        end: () => {},
      } as unknown as Sentry.Span;
      return callback(noopSpan);
    }

    return Sentry.startSpan(
      {
        op: spanOptions.op,
        name: spanOptions.name,
        attributes: spanOptions.attributes,
      },
      callback
    );
  }

  /**
   * Get the Sentry logger for structured logging
   */
  public get sentryLogger() {
    if (!this.isInitialized) {
      // Return a no-op logger if Sentry is not initialized
      return {
        trace: () => {},
        debug: () => {},
        info: () => {},
        warn: () => {},
        error: () => {},
        fatal: () => {},
        fmt: () => "",
      };
    }

    return Sentry.logger;
  }

  /**
   * Clear user context (e.g., on logout)
   */
  public clearUser(): void {
    if (!this.isInitialized) return;
    Sentry.setUser(null);
  }

  /**
   * Hash email for privacy while maintaining some tracking capability
   */
  private async hashEmail(email: string): Promise<string> {
    try {
      // Use SubtleCrypto for SHA-256 hashing for better privacy and uniqueness
      const msgBuffer = new TextEncoder().encode(email);
      const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      // Return first 12 characters for reasonable uniqueness while keeping it short
      return `user_${hashHex.substring(0, 12)}`;
    } catch (error) {
      // Fallback to simple hash if SubtleCrypto is not available
      let hash = 0;
      for (let i = 0; i < email.length; i++) {
        const char = email.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      return `user_${Math.abs(hash)}`;
    }
  }

  /**
   * Check if Sentry is initialized and working
   */
  public get isReady(): boolean {
    return this.isInitialized;
  }
}

// Export singleton instance
export const sentryManager = SentryManager.getInstance();

/**
 * Initialize Sentry - call this in main.tsx
 */
export const initializeSentry = (config?: SentryConfig): void => {
  sentryManager.initialize(config);
};

/**
 * Utility functions for easy access
 */
export const setUserContext = (user: {
  id?: string;
  email?: string;
  [key: string]: any;
}): Promise<void> => {
  return sentryManager.setUserContext(user);
};

export const setHotelTags = (context: {
  hotelId?: string;
  employeeId?: string;
  [key: string]: any;
}): void => {
  sentryManager.setHotelTags(context);
};

export const captureApiError = (
  error: any,
  meta: Record<string, any> = {}
): void => {
  sentryManager.captureApiError(error, meta);
};

export const captureNetworkError = (
  error: any,
  meta: Record<string, any> = {}
): void => {
  sentryManager.captureNetworkError(error, meta);
};

export const captureRenderError = (
  error: Error,
  errorInfo: { componentStack?: string }
): void => {
  sentryManager.captureRenderError(error, errorInfo);
};

export const captureGlobalError = (
  error: any,
  meta: Record<string, any> = {}
): void => {
  sentryManager.captureGlobalError(error, meta);
};

export const startSpan = <T>(
  spanOptions: { op: string; name: string; attributes?: Record<string, any> },
  callback: (span: Sentry.Span) => T
): T => {
  return sentryManager.startSpan(spanOptions, callback);
};

export const startAsyncSpan = <T>(
  spanOptions: { op: string; name: string; attributes?: Record<string, any> },
  callback: (span: Sentry.Span) => Promise<T>
): Promise<T> => {
  return sentryManager.startAsyncSpan(spanOptions, callback);
};

export const clearUser = (): void => {
  sentryManager.clearUser();
};

// Re-export Sentry for direct access when needed
export { Sentry };
